// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, Fragment } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Button from '../Button';
import FormField from '../FormField';
import Input from '../Input';
import Popover from '../Popover';
import Translate from '../Translate';
import passwordScore from './passwordScore';
import locale from './locale/en-US';

import './FormFieldPassword.less';

const symbolClasses = {
    upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lower: 'abcdefghijklmnopqrstuvwxyz',
    number: '0123456789',
    special: '!@#$%^&*?_~',
};

/**
 * `FormFieldPassword` component.
 */
class FormFieldPassword extends Component {
    constructor(props) {
        super(props);
        this.state = {
            visible: false,
            passwordMeterVisible: false,
        };
    }

    handleToggleClick = () => {
        this.setState(({ visible }) => ({
            visible: !visible,
        }));
    };

    handleBlur = () => {
        this.setState({
            passwordMeterVisible: false,
        });
    };

    generatePassword() {
        const password = [
            'upper', 'lower', 'lower', 'lower', 'number', 'number', 'special', 'number',
            'upper', 'upper', 'lower', 'lower', 'lower', 'lower', 'lower', 'lower',
        ];

        return password
            .sort((a, b) => Math.floor(b.length * Math.random()) - Math.floor(a.length * Math.random()))
            .map(symbolClass => symbolClasses[symbolClass][Math.floor(symbolClasses[symbolClass].length * Math.random())])
            .join('');
    }

    passwordScore(value) {
        if (!value) {
            return { intent: null, strength: null, unusedRules: [] };
        }

        const { score, unusedRules } = passwordScore(value);
        let intent = 'success';
        let strength = <Translate content="FormFieldPassword.strengthStrong" fallback={locale.strengthStrong} />;
        if (score < 25) {
            intent = 'danger';
            strength = <Translate content="FormFieldPassword.strengthWeak" fallback={locale.strengthWeak} />;
        } else if (score < 35) {
            intent = 'warning';
            strength = <Translate content="FormFieldPassword.strengthMedium" fallback={locale.strengthMedium} />;
        }

        return { intent, strength, unusedRules };
    }

    renderPasswordMeter(value, target) {
        const { passwordMeterVisible } = this.state;
        const { intent, strength, unusedRules } = this.passwordScore(value);

        return (
            <Popover
                visible={passwordMeterVisible && !!value}
                target={target}
                placement="bottom-right"
                intent={intent}
                canCloseOnOutsideClick={false}
                canCloseOnEscapePress={false}
            >
                <Translate
                    content="FormFieldPassword.passwordStrength"
                    fallback={locale.passwordStrength}
                    params={{
                        strength: <b>{strength}</b>,
                    }}
                />
                <br />
                {unusedRules.length ? (
                    <Fragment>
                        <Translate content="FormFieldPassword.improvePassword" fallback={locale.improvePassword} />
                        <br />
                        <ul>
                            {unusedRules.slice(0, 3).map(({ rule }) => (
                                <li key={rule}><Translate content={`FormFieldPassword.${rule}`} fallback={locale[rule]} /></li>
                            ))}
                        </ul>
                    </Fragment>
                ) : (
                    <Translate content="FormFieldPassword.yourPasswordIsStrong" fallback={locale.yourPasswordIsStrong} />
                )}
            </Popover>
        );
    }

    render() {
        const {
            baseClassName,
            className,
            hideShowButton,
            hideGenerateButton,
            hidePasswordMeter,
            ...props
        } = this.props;

        return (
            <FormField className={classNames(baseClassName, className)} {...props}>
                {({ getId, getName, getValue, setValue }) => {
                    const input = (
                        <Input
                            id={getId()}
                            name={getName()}
                            type={this.state.visible ? 'text' : 'password'}
                            value={getValue('')}
                            onChange={e => {
                                setValue(e.target.value);
                                this.setState({
                                    passwordMeterVisible: true,
                                });
                            }}
                            onFocus={this.handleFocus}
                            onBlur={this.handleBlur}
                        />
                    );

                    return (
                        <Fragment>
                            {hidePasswordMeter ? input : this.renderPasswordMeter(getValue(), input)}
                            {hideShowButton ? (
                                null
                            ) : (
                                <Button
                                    onClick={this.handleToggleClick}
                                    icon={this.state.visible ? 'visible' : 'invisible'}
                                    tooltip={this.state.visible ? (
                                        <Translate content="FormFieldPassword.hidePassword" fallback={locale.hidePassword} />
                                    ) : (
                                        <Translate content="FormFieldPassword.showPassword" fallback={locale.showPassword} />
                                    )}
                                />
                            )}
                            {hideGenerateButton ? (
                                null
                            ) : (
                                <Fragment>
                                    {' '}
                                    <Button
                                        onClick={() => setValue(this.generatePassword())}
                                        tooltip={<Translate content="FormFieldPassword.generateButtonHint" fallback={locale.generateButtonHint} />}
                                    >
                                        <Translate content="FormFieldPassword.generateButton" fallback={locale.generateButton} />
                                    </Button>
                                </Fragment>
                            )}
                        </Fragment>
                    );
                }}
            </FormField>
        );
    }
}
FormFieldPassword.propTypes = {
    /**
     * Is show button hidden?
     */
    hideShowButton: PropTypes.bool,
    /**
     * Is generate button hidden?
     */
    hideGenerateButton: PropTypes.bool,
    /**
     * Is password meter hidden?
     */
    hidePasswordMeter: PropTypes.bool,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
FormFieldPassword.defaultProps = {
    hideShowButton: false,
    hideGenerateButton: false,
    hidePasswordMeter: false,
    className: undefined,
    baseClassName: `${CLS_PREFIX}form-field-password`,
};

export default FormFieldPassword;



// WEBPACK FOOTER //
// ./components/FormFieldPassword/FormFieldPassword.js